import { forwardRef, createElement, useMemo, useState, useEffect, createContext, useContext, isValidElement } from 'react';
import cn from 'classnames';
import { IconButton } from '../IconButton/IconButton.js';
import { useLocalization } from '../Provider/Provider.js';
import { Root, Anchor as Anchor$1, Close } from '@radix-ui/react-popover';
import { UnstyledContent, UnstyledArrow } from '../Popover/Primitives.js';
import { mergeRefs } from '../../utils/mergeRefs.js';

const HangerContext = /*#__PURE__*/createContext({
  props: {},
  ref: null
});
const Anchor = /*#__PURE__*/forwardRef(function HangerAnchor(props, ref) {
  var _props$children;

  const context = useContext(HangerContext);
  let children = props.children;

  if (isValidElement(props.children) && typeof ((_props$children = props.children) === null || _props$children === void 0 ? void 0 : _props$children.type) === 'function') {
    console.warn(`Hanger.Anchor requires its child to forwardRef so that it can attach to the dom element. Did you mean to wrap '${props.children.type.name}' in React.forwardRef()? Taco has wrapped '${props.children.type.name}' in a 'span' to maintain functionality, but this may cause unintended behaviour`);
    children = createElement("span", null, props.children);
  }

  return createElement(Anchor$1, Object.assign({}, context.props, props, {
    children: children,
    ref: mergeRefs([context.ref, ref]),
    asChild: true
  }));
});
const Title = /*#__PURE__*/forwardRef(function DialogTitle(props, ref) {
  const className = cn('mb-1 text-base font-bold flex w-full', props.className);
  return createElement("span", Object.assign({}, props, {
    className: className,
    ref: ref
  }));
});
const Content = /*#__PURE__*/forwardRef(function HangerContent(props, ref) {
  const context = useContext(HangerContext);
  const {
    texts
  } = useLocalization();
  const className = cn('wcag-blue border border-transparent rounded p-3 pr-12 yt-shadow z-[996] focus:border-transparent max-w-sm', props.className);

  const handleInteractOutside = event => {
    event.preventDefault();
  };

  return createElement(UnstyledContent, {
    className: className,
    "data-taco": "hanger",
    onInteractOutside: handleInteractOutside,
    placement: props.placement,
    ref: ref
  }, props.children, createElement(UnstyledArrow, {
    className: "text-blue"
  }), createElement(Close, {
    asChild: true
  }, createElement(IconButton, {
    appearance: "primary",
    "aria-label": texts.hanger.close,
    className: "absolute top-0 right-0 ml-2 mr-2 mt-2 text-white",
    icon: "close",
    onClick: context.props.onClose
  })));
});
const Hanger = /*#__PURE__*/forwardRef(function Hanger(props, ref) {
  const {
    anchor,
    children,
    defaultOpen = true,
    ...otherProps
  } = props;
  const context = useMemo(() => ({
    props: otherProps,
    ref
  }), [otherProps]); // we do this to ensure hangers are mounted after their containers, e.g. if the container is another portal

  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (defaultOpen) {
      setOpen(defaultOpen);
    }
  }, []);
  return createElement(HangerContext.Provider, {
    value: context
  }, createElement(Root, {
    key: String(open),
    defaultOpen: open
  }, anchor && createElement(Anchor, null, anchor), children));
});
Hanger.Anchor = Anchor;
Hanger.Content = Content;
Hanger.Title = Title;

export { Hanger, Title };
//# sourceMappingURL=Hanger.js.map
